
import Vue from 'vue';
import { ContractModel, CourseModel, UserModel } from '@descomplica/dex-models';
import { DEFAULT_CATEGORY } from './helpers/category';
import { Loading, Button, Link } from '@descomplica/vue-components';
import { ENDPOINTS } from './settings/endpoints';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { handleLapRedirection } from '@/helpers/lap_to_classrooms_migration/redirectToLap';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { loadScriptsIfInTrial } from '@/helpers/scriptsTrial';
import redirectIfIsUndergrad from '@/helpers/redirectIfIsUndergrad';
import TermsOfUse from '@/components/TermsOfUse.vue';
import { SupportChatBot } from './helpers/supportChatBot';
import Enrollments from '@/services/Enrollments';
import ChatBotButton from '@/components/ChatBotButton.vue';
import ExpiredUserTrialModal from '@/components/modals/ExpiredUserTrialModal.vue';
import SelectiveProcessModal from '@/components/modals/showcase/SelectiveProcessModal.vue';
import { equalWebSiteKey, equalWebIntegrityKey } from '@/settings/constants';
import { PostgradAnalytics } from './helpers/analytics/postgradAnalytics';
import { startOf } from './helpers/date';
import { UndergradAnalytics } from './helpers/analytics/undergradAnalytics';
import CouponModal from '@/components/modals/showcase/CouponModal.vue';
import MicrocertificateModal from '@/components/modals/showcase/microcertificate/MicrocertificateModal.vue';
import { PostgradConsumptionHelper } from './helpers/postgradConsumption';
import { upsertGoogleOptimizeScript } from './helpers/googleOptimize';
import { DocumentsStatus, WarningLevels } from '@/enum/postgradStudentDocuments.enum';
import { StudentDocumentsStatusInfo } from './helpers/postgradStudentDocuments/interfaces';
import { isLinkedinOAuthCodeOnCurrentRoutePath } from './helpers/linkedin';
import { PostgradEarlyGraduationTrackingInformation } from './models/postgradEarlyGraduation/PostgradEarlyGraduationModel';

interface Data {
  loaded: boolean;
  displayMessageNoAccess?: string;
  alreadyHadAccess: boolean;
  categorySlug: string;
  isSupportChatOpen: boolean;
  timeouts: Array<number>;
  cookiesPolicyPrivacyLink: HTMLElement | null;
  cookiesButton: HTMLElement | null;
  shouldShowEqualWeb: boolean;
  isUndergraduate: boolean;
}

export default Vue.extend({
  components: {
    Loading,
    Button,
    Link,
    TermsOfUse,
    ExpiredUserTrialModal,
    CouponModal,
    SelectiveProcessModal,
    MicrocertificateModal,
    ChatBotButton,
  },
  data(): Data {
    return {
      loaded: false,
      displayMessageNoAccess: undefined,
      alreadyHadAccess: false,
      categorySlug: this.$route.params.category || DEFAULT_CATEGORY,
      isSupportChatOpen: false,
      timeouts: [],
      cookiesPolicyPrivacyLink: null,
      cookiesButton: null,
      shouldShowEqualWeb: false,
      isUndergraduate: false,
    };
  },
  computed: {
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    expiredChimeraCourseById(): ContractModel | null {
      return this.$store.getters.expiredChimeraCourseById(this.currentCourse.descomplicaCourseId);
    },
    isPostgraduateCourse(): boolean {
      return this.$store.getters.isPostgraduateCourse;
    },
    isUndergraduateCourse(): boolean {
      return this.$store.getters.isUndergraduateCourse;
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    chimeraCourses(): ChimeraCourseModel[] {
      return this.$store.getters.getChimeraUserCourses;
    },
    isMigrationRoute(): boolean {
      return this.$route.name === 'migration';
    },
    isLinkedinSharingRedirectionRoute(): boolean {
      return this.$route.fullPath.includes('/compartilhar');
    },
    isForumRoute(): boolean {
      return this.$router.currentRoute.fullPath.includes('forum');
    },
    isNotebooksRoute(): boolean {
      return this.$router.currentRoute.fullPath.includes('cadernos');
    },
    isSharedNotebookRoute(): boolean {
      return !!this.$route.params.notebookHash;
    },
    isSurveyRoute(): boolean {
      return this.$router.currentRoute.fullPath.includes('/pesquisa/');
    },
    alreadyHadAccessPhoto(): string {
      return `${process.env.BASE_URL || ''}img/already-had-access.svg`;
    },
    shouldShowEqualWebWidget(): boolean {
      return this.$store.getters.shouldShowEqualWebWidget;
    },
    allCourses(): CourseModel[] {
      return this.$store.getters.allCourses;
    },
    canViewPostgradStudentDocuments(): boolean {
      return this.$store.getters.canViewPostgradStudentDocuments;
    },
    isDocumentsUploadDelayToastVisible(): boolean {
      if (!this.documentStatus) {
        return false;
      }

      return (
        this.canViewPostgradStudentDocuments && this.documentStatus.status !== DocumentsStatus.OK
      );
    },
    documentStatus(): StudentDocumentsStatusInfo | null {
      return this.$store.getters.studentDocumentsStatusByCourseId(this.currentCourse.id);
    },
    canViewPostgradCertificateCallout(): boolean {
      return this.$store.getters.canViewPostgradCertificate;
    },
    showLinkedinOAuthMessage(): boolean {
      return isLinkedinOAuthCodeOnCurrentRoutePath(this.$route);
    },
    shouldGetTrial(): boolean {
      return this.$store.getters.shouldGetTrial;
    },
    isTrial(): boolean {
      return !!this.$store.getters.trialByDescomplicaCourseId(
        this.currentCourse.descomplicaCourseId,
      );
    },
    isCourseEligibleForEarlyGraduation(): boolean {
      return this.$store.getters.earlyGraduationEligibilityByCourseId(this.currentCourse.id);
    },
    earlyGraduationTrackingProps(): PostgradEarlyGraduationTrackingInformation {
      return this.$store.getters.earlyGraduationTrackings(this.currentChimeraCourse);
    },
  },
  async created() {
    await this.$store.dispatch('PULL_USER_INFO');

    this.setCurrentCourse();

    this.storeFirstContentConsumedDate();

    this.privacyPolicyListeners();

    const course = this.currentCourse;

    this.handleRouteRedirection(course);

    if (!course?.id) {
      this.alreadyHadAccess = await Enrollments.hasAlreadyUsedTrial();
      this.displayMessageNoAccess = this.$tc(
        `youDontHaveAccess${this.alreadyHadAccess ? '' : 'Yet'}`,
      );
    }

    if (!course) {
      this.handleDexCoursesEmpty();
      return;
    }

    await this.$store.dispatch('PULL_PERIOD_START_DATE', {});
    await this.$store.dispatch('SWITCH_CURRENT_COURSE', {
      course,
      user: this.user,
    });

    if (this.shouldGetTrial) {
      await this.$store.dispatch('GET_POSTGRAD_TRIAL', this.currentChimeraCourse);
    }

    if (this.isCourseEligibleForEarlyGraduation === undefined) {
      await this.$store.dispatch(
        'GET_POSTGRAD_EARLY_GRADUATION_ELIGIBILITY_FOR_COURSE',
        this.currentCourse.id,
      );
    }

    if (this.canViewPostgradStudentDocuments && !this.isTrial) {
      await this.$store.dispatch('GET_USER_DOCUMENTS_STATUS_BY_COURSE_ID', course);

      if (this.isDocumentsUploadDelayToastVisible) {
        this.trackToastViewed();
      }
    }

    if (this.canViewPostgradCertificateCallout && !this.isTrial) {
      this.$store.dispatch('GET_USER_CERTIFICATE_INFO_BY_COURSE_ID', course);
    }

    this.handleInAppNotifications();

    this.loaded = !handleLapRedirection({
      currentChimeraCourse: this.currentChimeraCourse,
      isMigrationRoute: this.isMigrationRoute,
    });

    if (this.isPostgraduateCourse) this.trackPostgradUserAccess();
    if (this.isUndergraduateCourse) this.trackUndergradUserAccess();

    loadScriptsIfInTrial();

    upsertGoogleOptimizeScript(course.descomplicaCategorySlug);

    if (this.shouldShowEqualWebWidget) {
      this.insertEqualWebScript();
    }

    this.checkPostgradExpiredContractRedirection();
  },
  beforeDestroy() {
    const { clearTimeout } = window;

    this.timeouts.map((timeout) => clearTimeout(timeout));

    if (this.cookiesPolicyPrivacyLink) {
      this.cookiesPolicyPrivacyLink.removeEventListener('click', this.trackPrivacyLinkListener);
    }

    if (this.cookiesButton) {
      this.cookiesButton.removeEventListener('click', this.trackPrivacyAcceptButtonListener);
    }
  },
  methods: {
    insertEqualWebScript(): void {
      window.interdeal = {
        sitekey: equalWebSiteKey,
        Position: 'Left',
        Menulang: 'PT',
        domains: {
          js: 'https://cdn.equalweb.com/',
          acc: 'https://access.equalweb.com/',
        },
        btnStyle: {
          vPosition: [null, '20%'],
          color: {
            main: '#2E850F',
            second: '#000000',
          },
          scale: [0.5],
          icon: {
            type: 1,
            shape: 'circle',
            outline: false,
          },
        },
      };

      const scriptElement = document.createElement('script');
      scriptElement.src = 'https://cdn.equalweb.com/core/4.2.2/accessibility.js';
      scriptElement.defer = true;
      scriptElement.integrity = equalWebIntegrityKey;
      scriptElement.crossOrigin = 'anonymous';
      scriptElement.setAttribute('data-cfasync', 'true');

      document.body.appendChild(scriptElement);
    },
    async storeFirstContentConsumedDate(): Promise<void> {
      const storedConsumptionInfo = PostgradConsumptionHelper.getStoredInfo();

      if (storedConsumptionInfo?.firstContentConsumedDate) {
        this.$store.commit('SET_FIRST_CONTENT_CONSUMED_DATE', {
          date: new Date(storedConsumptionInfo.firstContentConsumedDate),
        });

        return;
      }

      await this.$store.dispatch('GET_FIRST_CONTENT_CONSUMED_DATE', { userId: this.user.id });

      PostgradConsumptionHelper.storeInfo(
        'firstContentConsumedDate',
        this.$store.getters.firstContentConsumedDate,
      );
    },
    trackToastViewed(): void {
      Analytics.trackEvent(EVENT_LABELS.documentToastViewed, {
        status: DocumentsStatus[this.documentStatus!.status],
        warning_level: WarningLevels[this.documentStatus!.delayWarningLevel!],
        ...this.earlyGraduationTrackingProps,
      });
    },
    trackPrivacyLinkListener(): void {
      Analytics.trackEvent(EVENT_LABELS.linkNavigated, {
        label: 'Política de Privacidade',
        to: this.cookiesPolicyPrivacyLink?.getAttribute('href'),
      });
    },
    trackPrivacyAcceptButtonListener(): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        button: 'Aceite Cookies',
      });
    },
    privacyPolicyListeners(): void {
      this.cookiesPolicyPrivacyLink = document.getElementById('LGPD-cookies-alert-privacy-policy');
      this.cookiesButton = document.getElementById('LGPD-cookies-alert-button');

      if (this.cookiesPolicyPrivacyLink) {
        this.cookiesPolicyPrivacyLink.addEventListener('click', this.trackPrivacyLinkListener);
      }

      if (this.cookiesButton) {
        this.cookiesButton.addEventListener('click', this.trackPrivacyAcceptButtonListener);
      }
    },
    getStoredDexCourse(
      courseSlug: string,
      courses: CourseModel[],
      allowedCategories: string[],
    ): CourseModel | null {
      const courseBySlug = courseSlug ? this.user.getCourse(courseSlug) : null;
      if (courseBySlug?.id) return courseBySlug;

      const course =
        courses.find((courseItem: CourseModel) =>
          allowedCategories.includes(courseItem.descomplicaCategorySlug || ''),
        ) || this.user.getCourse();

      return course || null;
    },
    setCurrentCourse() {
      const courseSlug = this.$route.params.currentCourse;
      const allowedCategories: string[] = ['graduacao', 'pos', this.categorySlug];
      const courses: CourseModel[] = this.user.getAllCourses() ?? [];

      const currentCourse = this.getStoredDexCourse(courseSlug, courses, allowedCategories);

      this.$store.commit('SET_CURRENT_COURSE', currentCourse);
    },
    handleInAppNotifications() {
      this.$store.dispatch('INIT_NOTIFICATIONS');
    },
    handleDexCoursesEmpty() {
      if (this.chimeraCourses.length === 0) {
        return;
      }

      const [chimeraCourse] = this.chimeraCourses;
      window.location.replace(`${ENDPOINTS.lap}/cursos/${chimeraCourse.slug}`);
    },
    handleRouteRedirection(currentCourse: CourseModel | null) {
      if (this.isMigrationRoute || this.isLinkedinSharingRedirectionRoute || this.isSurveyRoute) {
        return;
      }

      if (!currentCourse) return;

      if (this.shouldRedirectValidRoute(currentCourse)) {
        if (redirectIfIsUndergrad(currentCourse)) return;

        this.$router.push({
          name: this.handleRouteName(),
          params: {
            category: currentCourse.descomplicaCategorySlug || this.categorySlug,
            currentCourse: currentCourse.slug || '',
          },
        });
      }
    },
    shouldRedirectValidRoute(currentCourse: CourseModel) {
      if (this.isSharedNotebookRoute || this.isSurveyRoute) {
        return false;
      }

      const category: string = currentCourse.descomplicaCategorySlug || this.categorySlug;

      return (
        this.$router.currentRoute.fullPath === '' ||
        this.$router.currentRoute.fullPath === '/' ||
        this.$router.currentRoute.fullPath === `/${category}` ||
        this.$router.currentRoute.fullPath === `/${category}/` ||
        this.$router.currentRoute?.params?.category !== category
      );
    },
    handleRouteName(): string {
      if (this.isNotebooksRoute) return 'notebooks';
      if (this.isForumRoute) return 'forum';
      if (this.isSurveyRoute) return 'survey';

      return 'home';
    },
    goToEnrollments(): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        button: 'to-enroll-no-access',
      });

      window.open(process.env.VUE_APP_ENROLLMENTS, '_self');
    },
    openChat(): void {
      if (SupportChatBot.isChatLoaded()) {
        Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
          button: 'open-chat-no-access',
        });

        SupportChatBot.openChatBot();
      }
    },
    async trackPostgradUserAccess(): Promise<void> {
      PostgradAnalytics.updateHubSpotContactProperties({
        last_classrooms_access_date: startOf(new Date()).getTime(),
      });
    },
    async trackUndergradUserAccess(): Promise<void> {
      UndergradAnalytics.updateHubSpotContactProperties({
        last_classrooms_access_date: startOf(new Date()).getTime(),
      });
    },
    checkPostgradExpiredContractRedirection(): void {
      if (this.isPostgraduateCourse && this.expiredChimeraCourseById) {
        this.$router.push({ name: 'expiredAccess' });
      }
    },
  },
});
